import Util                  from '../../utils/Util'
import AttributeValueFactory from "./AttributeValueFactory"

export default class Attribute {
    /**
     *
     * @param data
     * @param {Attribute|null} parent
     * @param {Array} children
     */
    constructor(data, parent = null, children = []) {
        this.parent = parent
        this.children = children
        this.data = data
        this.attributeValues = []

        let originalAttributeValues = this.getOriginalAttributeValues()

        for (let attributeValueData of originalAttributeValues) {
            this.attributeValues.push(AttributeValueFactory.create(attributeValueData))
        }
    }

    get(key, defaultValue = null) {
        return Util.getNestedProperty(this.data, key, defaultValue)
    }

    /**
     * Returns the parent node.
     *
     * @returns {*}
     */
    getParent() {
        return this.parent;
    }

    /**
     * Get the parent attribute id from the pivot object
     */
    getPivotAttributeParentId() {
        return this.get('pivot.attribute_parent_id', null)
    }

    /**
     * Sets the parent node.
     *
     * @param {Attribute} parentNode
     */
    setParent(parentNode) {
        this.parent = parentNode
    }

    /**
     * Returns all the children
     *
     * @returns {Array}
     */
    getChildren() {
        return this.children
    }

    /**
     * Insert a child node
     *
     * @param {Attribute} newNode
     */
    insertChild(newNode) {
        newNode.setParent(this)
        this.children.push(newNode);
    }

    /**
     * Removes a child node from the node and returns it.
     * If the child node does not exist, it returns null.
     *
     * @param removeNode
     * @returns {null|*}
     */
    removeChild(removeNode) {
        let index = this.hasImmediateChild(removeNode);

        if (index > -1) {
            let spliced = this.children.splice(index, 1)[0];
            spliced.setParent(null);

            return spliced;
        }

        return null;
    }

    /**
     * Checks if the given node is an immediate child of this node.
     * Returns the index or -1 if not found.
     *
     * @param {Attribute} node
     */
    hasImmediateChild(node) {
        for (let i = 0; i <= this.children.length; i++) {
            if (this.children[i] === node) {
                return i;
            }
        }

        return -1;
    }

    /**
     * Validates if the node has any children
     *
     * @returns {boolean}
     */
    hasChildren() {
        return this.children.length !== 0
    }

    /**
     * Get the ID from the data.
     *
     * @returns {*}
     */
    getId() {
        return this.get('id')
    }

    /**
     * Returns the ID of the parent node from the data
     *
     * @returns {null|number}
     */
    getParentId() {
        return this.get('pivot.attribute_parent_id')
    }

    /**
     * Get the display name form the data of the node.
     *
     * @returns {*}
     */
    getName() {
        return this.get('name', '')
    }

    /**
     * Returns the type of the attribute.
     *
     * @returns {*}
     */
    getType() {
        return this.get('type', '')
    }

    /**
     * Returns the slug of the node
     *
     * @returns {*}
     */
    getSlug() {
        return this.get('slug')
    }

    /**
     * Checks if the node has any attribute values
     *
     * @returns {boolean}
     */
    hasOriginalAttributeValues() {
        return this.getAttributeValues().length > 0
    }

    /**
     * Returns the attribute values or an empty array
     * @returns {*}
     */
    getOriginalAttributeValues() {
        return this.get('attribute_values', [])
    }

    /**
     * Returns the objectified attribute values
     *
     * @returns {Array}
     */
    getAttributeValues() {
        return this.attributeValues
    }

    /**
     * Gets the default value form the attribute values
     * @returns {null}
     */
    getDefaultAttributeValue() {
        let defaultAttributeValue = null

        for (let i = 0; i < this.attributeValues.length; i++) {
            let defaultAttributeValue = this.attributeValues[i].isDefault() ? this.attributeValues[i] : null

            if (defaultAttributeValue !== null) {
                return defaultAttributeValue
            }
        }

        return defaultAttributeValue
    }

    /**
     * Determines wheater any attribute values exist
     *
     * @returns {boolean}
     */
    hasAttributeValues() {
        return this.attributeValues.length > 0
    }

    /**
     * Checks if a thumbnail is set for the node
     *
     * @returns {boolean}
     */
    hasThumbnailUrl() {
        return this.get('thumbnail_url', '').length > 0
    }

    /**
     * Returns the thumbnail of the attribute node.
     *
     * @returns {String}
     */
    getThumbnailUrl() {
        return this.get('thumbnail_url', '')
    }

    /**
     * Returns the description for the attribute node.
     *
     * @returns {String}
     */
    getDescription() {
        return this.get('description', '')
    }

    /**
     * Verifies if the attribute is visible
     */
    isVisible() {
        return this.get('visible', false)
    }

}
